import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '../components/Layout';
import { Badge, Card, EmptyState, ErrorNote, Loading, Stat, StatusBadge } from '../components/ui';
import type { Dashboard as DashboardData } from '../lib/api';
import { channelLabel, date, percent, relative } from '../lib/format';
import { useApi } from '../lib/hooks';

export function Dashboard() {
  const { data, loading, error, reload } = useApi<DashboardData>('/api/analytics/dashboard');

  if (loading) return <Loading label="Loading your pipeline" />;
  if (error) return <ErrorNote message={error} onRetry={reload} />;
  if (!data) return null;

  const { funnel, engagement, timeline, hot_list: hotList, recent_activity: recent } = data;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description="Where every campaign stands, and what needs you today."
        helpHref="dashboard"
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat label="Prospects" value={funnel.prospects} sub={`${funnel.contacted} contacted`} />
        <Stat
          label="Replies"
          value={funnel.replied}
          sub={`${percent(funnel.reply_rate, 1)} of contacted`}
          tip="Prospects who replied, as a share of everyone who's been contacted."
        />
        <Stat
          label="Warm"
          value={funnel.warm}
          tone="positive"
          sub={`${percent(funnel.warm_rate)} of replies`}
          tip="Replies Claude classified as positive but non-committal — worth a follow-up, not yet a yes."
        />
        <Stat
          label="Won"
          value={funnel.won}
          tone="positive"
          sub={`${funnel.lost} lost`}
          tip="Prospects marked won or lost — the terminal states of the pipeline."
        />
        <Stat
          label="Drafts waiting"
          value={data.pending_drafts}
          tone={data.pending_drafts > 0 ? 'warning' : 'default'}
          sub={<Link to="/queue" className="text-accent-600 hover:underline">Open review queue</Link>}
          tip="Drafted messages that haven't been approved yet — nothing sends until an admin approves it."
        />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        <Card
          title="Last 30 days"
          className="xl:col-span-2"
          action={
            <span className="text-xs text-ink-500">
              {engagement.sent} sent · {percent(engagement.open_rate)} opened · {percent(engagement.click_rate)} clicked
            </span>
          }
        >
          {timeline.every((point) => point.sent === 0 && point.replies === 0) ? (
            <EmptyState
              title="No activity yet"
              description="Import prospects and send your first outreach to start seeing trends here."
            />
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeline} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="sentFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#337dff" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="#337dff" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="replyFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 4" stroke="#e6e9ef" vertical={false} />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 11, fill: '#8592ab' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value: string) => date(value).replace(/,.*/, '')}
                    minTickGap={24}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#8592ab' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                    width={40}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: '1px solid #e6e9ef',
                      fontSize: 12,
                      boxShadow: '0 4px 12px rgb(0 0 0 / 0.06)',
                    }}
                    labelFormatter={(value: string) => date(value)}
                  />
                  <Area
                    type="monotone"
                    dataKey="sent"
                    name="Sent"
                    stroke="#337dff"
                    strokeWidth={2}
                    fill="url(#sentFill)"
                  />
                  <Area
                    type="monotone"
                    dataKey="replies"
                    name="Replies"
                    stroke="#10b981"
                    strokeWidth={2}
                    fill="url(#replyFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card
          title={`Needs action (${hotList.length})`}
          action={
            <Link to="/replies" className="text-xs font-medium text-accent-600 hover:underline">
              All replies
            </Link>
          }
          bodyClassName="p-0"
        >
          {hotList.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-ink-500">
              Nothing waiting. New replies show up here with Claude's read on them.
            </p>
          ) : (
            <ul className="divide-y divide-ink-100">
              {hotList.map((reply) => (
                <li key={reply.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <Link
                      to={`/prospects/${reply.prospect_id}`}
                      className="text-sm font-medium text-ink-900 hover:text-accent-700"
                    >
                      {reply.prospect_name}
                    </Link>
                    <StatusBadge status={reply.intent} />
                  </div>
                  <p className="text-xs text-ink-500">{reply.company_name}</p>
                  <p className="mt-1.5 line-clamp-2 text-sm text-ink-600">{reply.message_body}</p>
                  {reply.recommended_next_step && (
                    <p className="mt-1.5 text-xs text-accent-700">
                      → {String(reply.recommended_next_step).replace(/^"|"$/g, '')}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-ink-400">{relative(reply.received_at)}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card title="Recent activity" className="mt-4" bodyClassName="p-0">
        {recent.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-ink-500">No outreach logged yet.</p>
        ) : (
          <ul className="divide-y divide-ink-100">
            {recent.map((activity) => (
              <li key={activity.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm">
                <StatusBadge status={activity.status} />
                <Link
                  to={`/prospects/${activity.prospect_id}`}
                  className="font-medium text-ink-900 hover:text-accent-700"
                >
                  {activity.prospect_name}
                </Link>
                <span className="text-ink-500">{activity.company_name}</span>
                <Badge>{channelLabel(activity.channel)}</Badge>
                <span className="text-xs text-ink-500">{activity.message_type.replace(/_/g, ' ')}</span>
                {activity.email_opened && <Badge tone="green">opened</Badge>}
                {activity.email_clicked && <Badge tone="blue">clicked</Badge>}
                {activity.campaign_name && (
                  <span className="text-xs text-ink-400">· {activity.campaign_name}</span>
                )}
                <span className="ml-auto text-xs text-ink-400">
                  {relative(activity.sent_at ?? activity.created_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
